import React from 'react'
import { Badge } from 'reactstrap'
import { useNavigate } from 'react-router-dom'
import cx from 'classnames'

import { CASE_TYPE, ROUTES, PENDING_ACTION_TYPE } from 'definitions'
import { formatDateByLanguage } from 'utils/formatters'

import Button from 'common/Button'
import Icon from 'common/Icon'
// ✅ MobX store access
import { useStores } from 'stores'
import Toaster from 'common/Toaster'

export default ({ resource, pendingAction, session }) => {
  const navigate = useNavigate()
  const { pendingActionType, overbillApprovalLink, hasPo } = pendingAction
  const { caseStore, globalMessageStore } = useStores()

  const showAbortButton = pendingActionType === PENDING_ACTION_TYPE.NO_PO_RECEIVED && !hasPo

  const getCaseType = type => {
    switch (type) {
      case CASE_TYPE.Connection:
        return { value: CASE_TYPE.Connection.toLowerCase(), label: 'Connections' }
      case CASE_TYPE.BeforeTheFacts:
      case CASE_TYPE.AfterTheFacts:
        return { value: 'po', label: 'PO' }
      case CASE_TYPE.ContractPo:
        return { value: 'contract-po', label: 'CONTRACT PO' }
      case CASE_TYPE.SelfService:
        return { value: 'self-service', label: 'DIY' }
      default:
        return { value: type.toLowerCase(), label: type }
    }
  }

  const handleViewItem = (event, caseType) => {
    event.stopPropagation()

    switch (pendingActionType) {
      case PENDING_ACTION_TYPE.OVERDUE_INVOICE:
        navigate(`${ROUTES.CASES}/${pendingAction.id}/salesinvoice`)
        break
      case PENDING_ACTION_TYPE.PROPOSAL_NOT_SELECTED:
        navigate(`${ROUTES.CASES}`, {
          state: { pendingActionId: pendingAction.id },
        })
        break
      case PENDING_ACTION_TYPE.OVERBILL_FOR_APPROVAL:
        if (overbillApprovalLink) window.open(overbillApprovalLink, '_blank')
        break
      case PENDING_ACTION_TYPE.PRA_FOR_APPROVAL:
        navigate(`${ROUTES.PRA}?praId=${pendingAction.id}`)
        break
      case PENDING_ACTION_TYPE.NO_PO_RECEIVED:
        if (caseType.label === CASE_TYPE.Webform) navigate(`${ROUTES.CASES}/${pendingAction.id}/propose`)
        else navigate(`${ROUTES.CASES}/${pendingAction.id}/pr`)
        break
    }
  }

  const caseType = getCaseType(pendingAction.type)

  const getDateToDisplay = pendingAction => {
    if (pendingActionType === PENDING_ACTION_TYPE.OVERDUE_INVOICE) {
      return pendingAction.salesInvoiceExpiryDate
    } else {
      return pendingAction.created
    }
  }

  // ✅ Direct Abort handler (no modal)
  const handleAbortCase = async e => {
    e.stopPropagation()

    const payload = {
      status: 'AbortedCancelledCase',
      reason: 'Aborted by customer',
      fromCaseItem: true,
    }
    Toaster.confirm({
      title: resource.AbortCase,
      message: resource.AbortCaseConfirmation,
      submitText: resource.Confirm,
      cancelText: resource.Cancel,
      closeOnSubmit: false,
      onSubmit: async () => {
        const success = await caseStore.abortCase(pendingAction.id, payload)
        if (success) {
          const caseLink = `<a href="/cases/${pendingAction.id}" rel="internal">${pendingAction.reference}</a>`

          const title = resource?.CaseAbortedTitle || 'Case Aborted'
          const messageTemplate = resource?.CaseAbortedMessage || 'Case {{CaseNumber}} aborted successfully.'
          const message = messageTemplate.replace('{{CaseNumber}}', caseLink)

          globalMessageStore.show({
            title,
            message,
            color: 'success',
            isHtml: true,
          })

          // ✅ Scroll smoothly to the top
          window.scrollTo({ top: 0, behavior: 'smooth' })

          // ✅ Wait 5 seconds, then reload
          setTimeout(() => {
            window.location.reload()
          }, 3000)
        }
      },
    })
  }

  return (
    <div className="actions col-xl-auto col ms-auto d-flex justify-content-end align-items-md-start align-items-xl-center align-items-end">
      <div className="col-auto mb-2">
        <Badge color="none" className={cx(`badge-case badge-case-${caseType.value}`)}>
          {caseType.label}
        </Badge>
      </div>
      <div className="col px-sm-3 px-0">
        <small className="text-muted d-flex flex-wrap flex-sm-row flex-column mb-xl-0 mb-2">
          <div className="d-flex align-items-center mb-1 me-2">{pendingAction.reference}</div>
          <div className="d-flex align-items-center mb-1 me-2">
            <Icon icon="person" filled className="md-12 me-1 text-muted" />
            <span className="requester-name">{pendingAction.requester.employeeName}</span>
          </div>
          <div className="d-flex align-items-center mb-1 me-2">
            <Icon icon="schedule" filled className="md-12 me-1 text-muted" />
            <span>{formatDateByLanguage(getDateToDisplay(pendingAction), 'dddd, D MMMM YYYY',session.uiLanguageSetting)}</span>
          </div>
          {pendingAction.poReference && (
            <div className="d-flex align-items-center mb-1 me-2">
              <Icon icon="confirmation_number" filled className="md-12 me-1 text-muted" />
              <span>{pendingAction.poReference}</span>
            </div>
          )}
        </small>
        <div className="fw-semibold word-break-break-word white-space-normal">{pendingAction.subject}</div>
      </div>

      {/* ✅ Show Abort button only for NO_PO_RECEIVED */}
      {showAbortButton && (
        <Button
          type="button"
          icon="block"
          color="danger"
          size="sm"
          className="with-text"
          onClick={handleAbortCase} // ✅ fixed handler call
        >
          <span className="d-sm-inline d-none">{resource.Abort}</span>
        </Button>
      )}

      {/* ✅ Show Email button only for NO_PO_RECEIVED */}
      {pendingActionType === PENDING_ACTION_TYPE.NO_PO_RECEIVED && (
        <a
          href={`mailto:${session.generalMailbox}?subject=${resource.MyCasesQuestionMailSubject?.replace(
            '{{CaseNumber}}',
            pendingAction.reference
          )}&body=${resource.MyCasesQuestionMailBody}`}
        >
          <Button
            type="button"
            icon="email"
            color="default"
            size="sm"
            onClick={e => e.stopPropagation()}
            title={resource.Email}
          />
        </a>
      )}

      <div className="actions">
        <Button
          type="button"
          icon={'visibility'}
          className={'me-2 with-text'}
          color={'default'}
          size="sm"
          onClick={event => handleViewItem(event, caseType)}
        >
          <span className="d-sm-inline d-none">{resource.View}</span>
        </Button>
      </div>
    </div>
  )
}
